import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { saveSession } from "@/lib/auth";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2, XCircle } from "lucide-react";

// Public onboarding wizard for an aliado invitation.
// Step 1: validate token, show a summary of the invitation.
// Step 2: set password + optional profile info → creates user + aliado + JWT.
export default function AliadosOnboarding() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [invitation, setInvitation] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [step, setStep] = useState(1);
  const [password, setPassword] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [handle, setHandle] = useState("");
  const [platform, setPlatform] = useState("linkedin");
  const [followerRange, setFollowerRange] = useState("10k-100k");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadError("Falta el token en la URL. Copia el link exacto del correo.");
      return;
    }
    axios
      .get(`${API_BASE}/public/aliados/onboarding`, { params: { token } })
      .then((r) => setInvitation(r.data))
      .catch((e) => {
        const detail = e.response?.data?.detail || "Invitación no encontrada o expirada.";
        setLoadError(String(detail));
      });
  }, [token]);

  const submit = async () => {
    setBusy(true);
    try {
      const body = { token, password, contact_phone: phone || undefined };
      if (invitation?.type === "reseller") {
        body.company_name = companyName || invitation.name;
      } else {
        body.handle = handle;
        body.platform = platform;
        body.follower_range = followerRange;
      }
      const { data } = await axios.post(`${API_BASE}/public/aliados/onboarding/complete`, body);
      if (data.status === "active" && data.access_token) {
        saveSession(data);
        toast.success("Bienvenido a DCC · Distribución");
        nav(invitation.type === "reseller" ? "/app/reseller" : "/app/influencer");
      } else {
        toast.info("Tu solicitud está en revisión. Te avisaremos por email.");
        nav("/login?pending=aliado");
      }
    } catch (e) {
      const detail = e.response?.data?.detail || "No se pudo completar el alta.";
      toast.error(String(detail));
    } finally {
      setBusy(false);
    }
  };

  if (loadError) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <div className="p-6"><Logo /></div>
        <div className="flex-1 flex items-center justify-center px-4">
          <div data-testid="aliados-onboarding-error" className="max-w-md text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto" />
            <h1 className="mt-4 text-2xl font-bold text-[#0B0B0B]">Invitación inválida</h1>
            <p className="mt-2 text-sm text-gray-600">{loadError}</p>
            <a href="/contacto" className="mt-6 inline-block text-sm text-[#F26A21] font-semibold">
              Contáctanos si crees que hay un error →
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (!invitation) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center text-gray-600">
        Verificando tu invitación…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="p-6"><Logo /></div>
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-lg" data-testid="aliados-onboarding-wizard">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#F26A21] font-semibold">
            <ShieldCheck className="w-4 h-4" /> Alta de {invitation.type === "reseller" ? "Reseller" : "Influencer"}
          </div>
          <h1 className="mt-2 text-3xl font-bold text-[#0B0B0B] font-display">
            Hola {invitation.name}
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Fuiste invitado a distribuir programas de Debt Capital College.
            Comisión inicial: <strong>{invitation.commission_pct}%</strong>.
          </p>

          <div className="mt-8 flex items-center gap-2 text-xs">
            <span
              className={`w-6 h-6 flex items-center justify-center rounded-full font-bold ${
                step >= 1 ? "bg-[#F26A21] text-white" : "bg-gray-200 text-gray-500"
              }`}
            >1</span>
            <span className="text-gray-600">Revisar invitación</span>
            <span className="w-8 h-px bg-gray-300" />
            <span
              className={`w-6 h-6 flex items-center justify-center rounded-full font-bold ${
                step >= 2 ? "bg-[#F26A21] text-white" : "bg-gray-200 text-gray-500"
              }`}
            >2</span>
            <span className="text-gray-600">Completar perfil</span>
          </div>

          {step === 1 && (
            <div className="mt-8 p-6 bg-[#F9FAFB] border border-gray-200 rounded-2xl">
              <p className="text-xs uppercase tracking-widest text-gray-500">Tu invitación</p>
              <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <dt className="text-gray-500">Correo</dt><dd className="font-mono text-[#0B0B0B]">{invitation.email}</dd>
                <dt className="text-gray-500">Rol</dt><dd className="capitalize text-[#0B0B0B]">{invitation.type}</dd>
                <dt className="text-gray-500">Comisión</dt><dd className="text-[#0B0B0B]">{invitation.commission_pct}%</dd>
                <dt className="text-gray-500">Expira</dt><dd className="text-[#0B0B0B]">{new Date(invitation.expires_at).toLocaleDateString("es-MX")}</dd>
              </dl>
              <button
                onClick={() => setStep(2)}
                data-testid="aliados-onboarding-continue-btn"
                className="mt-6 w-full py-3 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold text-sm"
              >
                Continuar
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="mt-8 p-6 bg-[#F9FAFB] border border-gray-200 rounded-2xl">
              <p className="text-xs uppercase tracking-widest text-gray-500">Completa tu perfil</p>
              <div className="mt-4 space-y-3">
                <Field label="Contraseña *" value={password} on={setPassword} type="password"
                  hint="Mínimo 8 caracteres" testid="ao-password" />
                {invitation.type === "reseller" && (
                  <Field label="Nombre de tu empresa" value={companyName} on={setCompanyName}
                    placeholder={invitation.name} testid="ao-company" />
                )}
                {invitation.type === "influencer" && (
                  <>
                    <Field label="Handle (LinkedIn, IG, etc.)" value={handle} on={setHandle} testid="ao-handle" />
                    <div>
                      <label className="text-[11px] uppercase tracking-widest text-gray-600 font-semibold">Plataforma</label>
                      <select value={platform} onChange={(e) => setPlatform(e.target.value)}
                        data-testid="ao-platform"
                        className="w-full mt-1 bg-white border border-gray-200 rounded-md px-3 py-2 text-sm text-[#0B0B0B]">
                        {["linkedin", "instagram", "tiktok", "youtube", "other"].map((p) =>
                          <option key={p} value={p}>{p}</option>
                        )}
                      </select>
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-widest text-gray-600 font-semibold">Rango de seguidores</label>
                      <select value={followerRange} onChange={(e) => setFollowerRange(e.target.value)}
                        data-testid="ao-followers"
                        className="w-full mt-1 bg-white border border-gray-200 rounded-md px-3 py-2 text-sm text-[#0B0B0B]">
                        {["<10k", "10k-100k", "100k-1M", ">1M"].map((r) =>
                          <option key={r} value={r}>{r}</option>
                        )}
                      </select>
                    </div>
                  </>
                )}
                <Field label="Teléfono (opcional)" value={phone} on={setPhone} testid="ao-phone" />
              </div>
              <div className="mt-6 flex items-center justify-between gap-3">
                <button
                  onClick={() => setStep(1)}
                  className="text-sm text-gray-500 hover:text-gray-800"
                >← Volver</button>
                <button
                  onClick={submit}
                  disabled={busy || !password || password.length < 8}
                  data-testid="aliados-onboarding-submit-btn"
                  className="px-6 py-3 rounded-md bg-[#F26A21] hover:bg-[#D9541A] disabled:opacity-50 text-[#0B0B0B] font-semibold text-sm inline-flex items-center gap-2"
                >
                  <CheckCircle2 className="w-4 h-4" /> {busy ? "Registrando…" : "Completar alta"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, on, type = "text", hint, placeholder, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-gray-600 font-semibold">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => on(e.target.value)}
        className="w-full mt-1 bg-white border border-gray-200 rounded-md px-3 py-2 text-sm text-[#0B0B0B] focus:outline-none focus:border-[#F26A21]"
      />
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}
